/**
 * dd-custom-gate.js
 *
 * Single source of truth for MTO custom-order acceptance.
 * required  ← dd-swatch-selector.isCustomSelection()
 * accepted  ← session flag (preserved across custom→custom; reset on leave)
 *
 * The right-side .dd-toggle both accepts and expands the terms.
 * Does NOT use data-dd-gate (that attribute is option-group names only).
 */
(function () {
  'use strict';

  if (window.ddCustomGate) return;

  var accepted = false;
  var wasRequired = false;
  var shakeTimer = null;
  var disclosureMs = 300;

  function prefersReducedMotion() {
    try {
      return window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    } catch (err) {
      return false;
    }
  }

  function syncDisclosureAria(details) {
    var summary = details.querySelector('summary');
    if (!summary) return;
    summary.setAttribute('aria-expanded', details.classList.contains('is--open') ? 'true' : 'false');
  }

  function setDisclosureOpen(details, open) {
    if (!details) return;
    if (details._ddDisclosureTimer) {
      clearTimeout(details._ddDisclosureTimer);
      details._ddDisclosureTimer = null;
    }

    if (open) {
      details.setAttribute('open', '');
      details.classList.add('is--open');
      syncDisclosureAria(details);
      return;
    }

    details.classList.remove('is--open');
    syncDisclosureAria(details);

    var delay = prefersReducedMotion() ? 0 : disclosureMs;
    if (delay === 0) {
      details.removeAttribute('open');
      return;
    }

    details._ddDisclosureTimer = setTimeout(function () {
      details.removeAttribute('open');
      details._ddDisclosureTimer = null;
    }, delay);
  }

  function syncDisclosuresFromAccepted() {
    document.querySelectorAll('[data-dd-custom-acceptance]').forEach(function (row) {
      if (row.hidden) {
        setDisclosureOpen(row, false);
        return;
      }
      setDisclosureOpen(row, accepted);
    });
  }

  function initDisclosures() {
    document.querySelectorAll('[data-dd-custom-acceptance]').forEach(function (details) {
      setDisclosureOpen(details, accepted && !details.hidden);
    });
    document.querySelectorAll('[data-dd-texture-warning-disclosure]').forEach(function (details) {
      if (details.hasAttribute('open') || details.classList.contains('is--open')) {
        details.setAttribute('open', '');
        details.classList.add('is--open');
      } else {
        details.classList.remove('is--open');
        details.removeAttribute('open');
      }
      syncDisclosureAria(details);
    });
  }

  function toggleDisclosureFromSummary(summary) {
    var details = summary.closest('[data-dd-texture-warning-disclosure]');
    if (!details || details.hidden) return;
    setDisclosureOpen(details, !details.classList.contains('is--open'));
  }

  function getSwatch() {
    return document.querySelector('dd-swatch-selector');
  }

  function isRequired() {
    var sw = getSwatch();
    if (!sw || typeof sw.isCustomSelection !== 'function' || !sw.isCustomSelection()) {
      return false;
    }
    /* Don't ask for MTO acceptance on a combo the brand doesn't sell. */
    if (
      typeof sw.isCurrentComboUnavailable === 'function' &&
      sw.isCurrentComboUnavailable()
    ) {
      return false;
    }
    return true;
  }

  function isBlocked() {
    if (!document.querySelector('[data-dd-custom-acceptance]')) return false;
    return isRequired() && !accepted;
  }

  function syncCheckboxes() {
    document.querySelectorAll('[data-dd-custom-acceptance-check]').forEach(function (cb) {
      cb.checked = accepted;
    });
  }

  /*
    "In stock" ledger row: the shopper picked a made-to-order length,
    but the same color may ship now at other standard lengths. Reads
    dd-swatch-selector.getInStockLengths() (shared with the swatch-card
    "Available in" hint and the ATC face) so all three surfaces name
    the same lengths. Hidden when nothing at this color ships now.
    Exported as ddCustomGate.syncInStockRow; dd-preorder-gate.js paints
    the same row on the pre-order panel with it.
  */
  function joinLengths(lengths) {
    if (lengths.length === 1) return lengths[0];
    if (lengths.length === 2) return lengths[0] + ' or ' + lengths[1];
    return lengths.slice(0, -1).join(', ') + ' or ' + lengths[lengths.length - 1];
  }

  function syncInStockRow(row) {
    var line = row.querySelector('[data-dd-custom-acceptance-instock]');
    if (!line) return;
    var sw = getSwatch();
    var lengths = sw && typeof sw.getInStockLengths === 'function' ? sw.getInStockLengths() : [];
    if (!lengths.length) {
      line.hidden = true;
      return;
    }
    var out = line.querySelector('[data-dd-custom-acceptance-instock-lengths]');
    if (out) out.textContent = joinLengths(lengths);
    line.hidden = false;
  }

  function syncRows() {
    var required = isRequired();
    document.querySelectorAll('[data-dd-custom-acceptance]').forEach(function (row) {
      if (required) {
        syncInStockRow(row);
        row.hidden = false;
        row.removeAttribute('hidden');
      } else {
        row.hidden = true;
        row.setAttribute('hidden', '');
        row.removeAttribute('open');
        row.classList.remove('is--open');
        if (row._ddDisclosureTimer) {
          clearTimeout(row._ddDisclosureTimer);
          row._ddDisclosureTimer = null;
        }
      }
    });
  }

  function atcBlocked() {
    if (isBlocked()) return true;
    if (
      window.ddPreorderGate &&
      typeof window.ddPreorderGate.isBlocked === 'function' &&
      window.ddPreorderGate.isBlocked()
    ) {
      return true;
    }
    return false;
  }

  function applyAtcFaces() {
    var blocked = atcBlocked();

    document.querySelectorAll('[data-dd-sf-atc]').forEach(function (btn) {
      if (btn.tagName === 'A' || btn.disabled) return;
      if (btn.classList.contains('is--availability-only')) return;
      if (blocked) {
        btn.setAttribute('aria-disabled', 'true');
        btn.classList.add('is--custom-acceptance-required');
      } else if (btn.classList.contains('is--custom-acceptance-required')) {
        btn.classList.remove('is--custom-acceptance-required');
        btn.removeAttribute('aria-disabled');
      }
    });

    document.querySelectorAll('.product-form__submit.dd-atc-button').forEach(function (btn) {
      if (btn.disabled) return;
      if (blocked) {
        btn.setAttribute('aria-disabled', 'true');
        btn.classList.add('is--custom-acceptance-required');
      } else if (btn.classList.contains('is--custom-acceptance-required')) {
        btn.classList.remove('is--custom-acceptance-required');
        if (!btn.classList.contains('loading')) {
          btn.removeAttribute('aria-disabled');
        }
      }
    });
  }

  function setAccepted(next) {
    accepted = !!next;
    syncCheckboxes();
    syncDisclosuresFromAccepted();
    applyAtcFaces();
  }

  function refresh() {
    var required = isRequired();
    if (wasRequired && !required) {
      accepted = false;
    }
    wasRequired = required;
    syncRows();
    syncCheckboxes();
    syncDisclosuresFromAccepted();
    applyAtcFaces();
  }

  function expandStickyIfCollapsed() {
    var footer = document.querySelector('dd-sticky-footer');
    if (!footer) return;
    if (footer.classList.contains('is--collapsed')) {
      footer.classList.remove('is--collapsed');
      try {
        sessionStorage.setItem('dd_sticky_collapsed', 'false');
      } catch (e) { /* ignore */ }
    }
  }

  function isElementOnScreen(el) {
    if (!el) return false;
    var rect = el.getBoundingClientRect();
    if (rect.width <= 0 || rect.height <= 0) return false;
    var vh = window.innerHeight || document.documentElement.clientHeight || 0;
    return rect.bottom > 0 && rect.top < vh;
  }

  /*
    Resolve which acceptance card to nudge. Prefer the surface the
    shopper just clicked (main ATC vs sticky ATC) so the amber shake
    lands on the checkbox they can see, not the off-screen twin.
  */
  function resolveAcceptanceRow(source) {
    var main = document.querySelector(
      '.dd-custom-acceptance--main:not([hidden]), .product-form [data-dd-custom-acceptance]:not([hidden])'
    );
    var sticky = document.querySelector(
      'dd-sticky-footer [data-dd-custom-acceptance]:not([hidden])'
    );

    var prefer = null;
    if (source === 'main') prefer = 'main';
    else if (source === 'sticky') prefer = 'sticky';
    else if (source && source.nodeType === 1) {
      if (source.closest('dd-sticky-footer') || source.closest('[data-dd-sf-atc]')) {
        prefer = 'sticky';
      } else if (source.closest('product-form') || source.closest('.dd-block--buy-buttons')) {
        prefer = 'main';
      }
    }

    if (prefer === 'main' && main) return main;
    if (prefer === 'sticky' && sticky) return sticky;

    if (main && isElementOnScreen(main)) return main;
    if (sticky && isElementOnScreen(sticky)) return sticky;
    return main || sticky || null;
  }

  function shakeAndFocus(source) {
    var row = resolveAcceptanceRow(source);
    if (!row) return;

    if (row.closest('dd-sticky-footer')) {
      expandStickyIfCollapsed();
    }

    row.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
    row.classList.remove('is--shake');
    // eslint-disable-next-line no-unused-expressions
    row.offsetWidth;
    row.classList.add('is--shake');

    if (shakeTimer) clearTimeout(shakeTimer);
    shakeTimer = setTimeout(function () {
      row.classList.remove('is--shake');
    }, 950);

    var check = row.querySelector('[data-dd-custom-acceptance-check]');
    if (check && typeof check.focus === 'function') {
      try {
        check.focus({ preventScroll: true });
      } catch (e) {
        check.focus();
      }
    }
  }

  function onDocClick(e) {
    var t = e.target;
    if (!t || !t.closest) return;

    if (t.matches && t.matches('[data-dd-custom-acceptance-check]')) {
      e.stopPropagation();
      e.preventDefault();
      setAccepted(!t.checked);
      return;
    }

    var wrap = t.closest('[data-dd-custom-acceptance-check-wrap]');
    if (wrap) {
      e.stopPropagation();
      e.preventDefault();
      setAccepted(!accepted);
      return;
    }

    var warningSummary = t.closest('.dd-texture-expansion__warning-summary');
    if (warningSummary && warningSummary.closest('[data-dd-texture-warning-disclosure]')) {
      e.preventDefault();
      toggleDisclosureFromSummary(warningSummary);
      return;
    }

    var summary = t.closest('.dd-custom-acceptance__summary');
    if (summary && summary.closest('[data-dd-custom-acceptance]')) {
      e.preventDefault();
      setAccepted(!accepted);
    }
  }

  function onDocKeydown(e) {
    if (e.key !== 'Enter' && e.key !== ' ') return;
    var t = e.target;
    if (!t || !t.closest) return;
    if (t.closest('[data-dd-custom-acceptance-check-wrap]')) return;
    if (t.matches && t.matches('[data-dd-custom-acceptance-check]')) return;

    var warningSummary = t.closest('.dd-texture-expansion__warning-summary');
    if (warningSummary && warningSummary.closest('[data-dd-texture-warning-disclosure]')) {
      e.preventDefault();
      toggleDisclosureFromSummary(warningSummary);
      return;
    }

    var summary = t.closest('.dd-custom-acceptance__summary');
    if (!summary || !summary.closest('[data-dd-custom-acceptance]')) return;
    e.preventDefault();
    setAccepted(!accepted);
  }

  function onDocChange(e) {
    var t = e.target;
    if (!t || !t.matches || !t.matches('[data-dd-custom-acceptance-check]')) return;
    setAccepted(!!t.checked);
  }

  function bind() {
    document.addEventListener('click', onDocClick, true);
    document.addEventListener('keydown', onDocKeydown, true);
    document.addEventListener('change', onDocChange);

    document.addEventListener('variantChange', function () {
      requestAnimationFrame(refresh);
    });
    /* Full variant set (GraphQL) can add in-stock lengths the 250-cap
       Liquid seed did not carry; repaint the "In stock" row. */
    document.addEventListener('dd:variants-full', function () {
      requestAnimationFrame(refresh);
    });

    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', function () {
        initDisclosures();
        refresh();
      });
    } else {
      initDisclosures();
      refresh();
    }
    setTimeout(function () {
      initDisclosures();
      refresh();
    }, 0);
    setTimeout(refresh, 400);
  }

  window.ddCustomGate = {
    isRequired: isRequired,
    isBlocked: isBlocked,
    setAccepted: setAccepted,
    refresh: refresh,
    applyAtcFaces: applyAtcFaces,
    shakeAndFocus: shakeAndFocus,
    initDisclosures: initDisclosures,
    syncInStockRow: syncInStockRow,
  };

  bind();
})();
